import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { dogs as staticDogs } from '../data/dogs';
import { fetchDogs } from '../lib/api';
import { useSupabaseData } from '../lib/useSupabaseData';
import { img } from '../lib/img';
import FloralBorder from '../components/FloralBorder';
import PhotoGallery from '../components/PhotoGallery';
import { Reveal, RevealHeading } from '../components/Reveal';

const DogProfilePage = () => {
  const { dogId } = useParams();
  const { i18n, t } = useTranslation();
  const { data: dogs, loading } = useSupabaseData(fetchDogs, staticDogs, 'dogs');

  const dog = dogs.find((d) => d.id === dogId);

  if (!dog) {
    // Jeszcze ładujemy — nic nie renderuj; po załadowaniu przekieruj gdy brak psa
    if (loading) return null;
    return <Navigate to="/nasze-psy" replace />;
  }

  const description = i18n.language === 'pl' ? dog.description.pl : dog.description.en;

  return (
    <div className="animate-fadeIn mx-auto max-w-5xl px-4">
      <section className="my-12 md:my-20">
        <div className="mb-12 text-center">
          <RevealHeading as="h1" text={dog.fullName} className="text-4xl text-brand-accent md:text-5xl" />
          {dog.birthDate && (
            <Reveal as="p" delay={0.18} className="mt-2 text-lg text-brand-text-light/70">{dog.birthDate}</Reveal>
          )}
          {dog.origin && (
            <Reveal as="p" delay={0.26} className="mt-1 text-md italic text-brand-accent/80">{dog.origin}</Reveal>
          )}
        </div>

        <div className="mb-14 flex justify-center">
          <motion.div
            layoutId={`dog-image-${dogId}`}
            className="h-64 w-64 overflow-hidden rounded-full bg-brand-bg-side shadow-2xl ring-1 ring-brand-accent/25 sm:h-80 sm:w-80"
          >
            <img
              src={img(dog.profileImage, { w: 600 })}
              alt={dog.name}
              decoding="async"
              className="h-full w-full object-cover"
            />
          </motion.div>
        </div>

        <RevealHeading as="h2" text={t('ourDogs.profile.gallery_title')} className="mb-8 text-center text-3xl text-brand-accent" />
        <div className="mb-14">
          <PhotoGallery
            images={dog.images}
            alt={dog.name}
            layout="grid"
            thumbWidth={500}
            emptyText={t('ourDogs.profile.no_images')}
          />
        </div>

        <Reveal className="mx-auto max-w-3xl">
          <FloralBorder>
            <div className="whitespace-pre-wrap leading-relaxed text-brand-text-light/85">{description}</div>
          </FloralBorder>
        </Reveal>

        {dog.pedigreeImage && (
          <div className="mt-14">
            <RevealHeading as="h2" text={t('ourDogs.profile.pedigree_title')} className="mb-8 text-center text-3xl text-brand-accent" />
            <PhotoGallery
              images={[dog.pedigreeImage]}
              alt={`${t('ourDogs.profile.pedigree_title')} ${dog.name}`}
              layout="masonry"
              thumbWidth={1200}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default DogProfilePage;
